const escapeHtml = (value: string) =>
  value
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;');

export type WikiLinkResolver = (target: string) => string | null;

const inlineMarkdown = (value: string, linkResolver?: WikiLinkResolver) =>
  escapeHtml(value)
    .replace(/`([^`]+)`/g, '<code>$1</code>')
    .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
    .replace(/__([^_]+)__/g, '<strong>$1</strong>')
    .replace(/(?<!\*)\*([^*]+)\*(?!\*)/g, '<em>$1</em>')
    .replace(/(?<!_)_([^_]+)_(?!_)/g, '<em>$1</em>')
    .replace(
      /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g,
      '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>',
    )
    .replace(
      /\[\[([^\]|#]+)(?:\|([^\]]+))?\]\]/g,
      (_match, rawTarget: string, rawLabel?: string) => {
        const target = rawTarget.trim();
        const label = (rawLabel ?? target).trim();
        const href = linkResolver?.(target);
        if (href) {
          return `<a href="${escapeHtml(href)}" data-wikilink="${escapeHtml(target)}">${escapeHtml(label)}</a>`;
        }
        return `<span class="wikilink-missing" data-wikilink="${escapeHtml(target)}">${escapeHtml(label)}</span>`;
      },
    );

function splitTableRow(line: string) {
  let trimmed = line.trim();
  if (trimmed.startsWith('|')) trimmed = trimmed.slice(1);
  if (trimmed.endsWith('|')) trimmed = trimmed.slice(0, -1);
  return trimmed.split('|').map((cell) => cell.trim());
}

function tableAlignments(separator: string) {
  return splitTableRow(separator).map((cell) => {
    const left = cell.startsWith(':');
    const right = cell.endsWith(':');
    if (left && right) return 'center';
    if (right) return 'right';
    return 'left';
  });
}

function isTableSeparator(line: string) {
  const cells = splitTableRow(line);
  return cells.length > 0 && cells.every((cell) => /^:?-{3,}:?$/.test(cell));
}

function looksLikeTableRow(line: string) {
  const trimmed = line.trim();
  return trimmed.includes('|') && !/^[-*+>\d]/.test(trimmed);
}

function renderTable(lines: string[], linkResolver?: WikiLinkResolver) {
  if (lines.length < 2 || !isTableSeparator(lines[1]!)) return null;
  if (!lines.every(looksLikeTableRow)) return null;

  const alignments = tableAlignments(lines[1]!);
  const header = splitTableRow(lines[0]!);
  const body = lines.slice(2).map(splitTableRow);
  const width = Math.max(header.length, ...body.map((row) => row.length), alignments.length);

  const alignAttr = (index: number) => {
    const align = alignments[index] ?? 'left';
    return align === 'left' ? '' : ` style="text-align:${align}"`;
  };

  const head = Array.from({ length: width }, (_, index) => {
    return `<th${alignAttr(index)}>${inlineMarkdown(header[index] ?? '', linkResolver)}</th>`;
  }).join('');

  const rows = body
    .map((row) => {
      const cells = Array.from({ length: width }, (_, index) => {
        return `<td${alignAttr(index)}>${inlineMarkdown(row[index] ?? '', linkResolver)}</td>`;
      }).join('');
      return `<tr>${cells}</tr>`;
    })
    .join('');

  return `<table><thead><tr>${head}</tr></thead>${rows ? `<tbody>${rows}</tbody>` : ''}</table>`;
}

/** Escape-first markdown → HTML for vault previews (no raw HTML passthrough). */
export function renderMarkdown(source: string, linkResolver?: WikiLinkResolver): string {
  const inline = (value: string) => inlineMarkdown(value, linkResolver);
  const normalized = source.replace(/\r\n?/g, '\n');
  const fences: string[] = [];
  const withFences = normalized.replace(
    /```([^\n]*)\n([\s\S]*?)```/g,
    (_, lang: string, code: string) => {
      const index = fences.length;
      const language = escapeHtml(lang.trim());
      fences.push(
        `<pre><code${language ? ` class="language-${language}"` : ''}>${escapeHtml(code.replace(/\n$/, ''))}</code></pre>`,
      );
      return `\n%%FENCE_${index}%%\n`;
    },
  );

  const blocks = withFences.split(/\n{2,}/);
  const html = blocks
    .map((block) => {
      const trimmed = block.trim();
      if (!trimmed) return '';

      const fence = trimmed.match(/^%%FENCE_(\d+)%%$/);
      if (fence) return fences[Number(fence[1])] ?? '';

      if (/^(-{3,}|\*{3,}|_{3,})$/.test(trimmed)) return '<hr />';

      const heading = trimmed.match(/^(#{1,6})\s+(.+)$/);
      if (heading && !trimmed.includes('\n')) {
        const marks = heading[1] ?? '#';
        const title = heading[2] ?? '';
        const level = marks.length;
        return `<h${level}>${inline(title)}</h${level}>`;
      }

      const lines = trimmed.split('\n');
      const table = renderTable(lines, linkResolver);
      if (table) return table;

      if (lines.every((line) => /^[-*+]\s+/.test(line))) {
        const items = lines
          .map((line) => `<li>${inline(line.replace(/^[-*+]\s+/, ''))}</li>`)
          .join('');
        return `<ul>${items}</ul>`;
      }
      if (lines.every((line) => /^\d+\.\s+/.test(line))) {
        const items = lines
          .map((line) => `<li>${inline(line.replace(/^\d+\.\s+/, ''))}</li>`)
          .join('');
        return `<ol>${items}</ol>`;
      }
      if (lines.every((line) => /^>\s?/.test(line))) {
        const body = lines.map((line) => inline(line.replace(/^>\s?/, ''))).join('<br />');
        return `<blockquote>${body}</blockquote>`;
      }

      return `<p>${lines.map((line) => inline(line)).join('<br />')}</p>`;
    })
    .filter(Boolean)
    .join('');

  return html.replace(/%%FENCE_(\d+)%%/g, (_, index: string) => fences[Number(index)] ?? '');
}

/** Collect [[wikilink]] targets from markdown source. */
export function extractWikiLinks(source: string): string[] {
  const links: string[] = [];
  const pattern = /\[\[([^\]|#]+)(?:\|[^\]]+)?\]\]/g;
  let match: RegExpExecArray | null;
  while ((match = pattern.exec(source))) {
    const target = match[1]?.trim();
    if (target) links.push(target);
  }
  return [...new Set(links)];
}
